import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { SkeletonLoading } from '@saleshandy/design-system';

import api from '../../../../../../api';
import type { IProps } from './do-not-call-list-details-container';

import {
  DoNotCallList,
  DoNotCallListPhoneNumber,
} from '../../../../types/do-not-call-list';
import toaster from '../../../../../../shared/toaster';
import DeleteButton from '../../../../../../shared/design-system/components/atoms/delete-button';
import ExportButton from '../../../../../../shared/design-system/components/atoms/export-button/export-button';

import AddModifyDoNotCallList from '../modals/add-modify-do-not-call-list/add-modify-do-not-call-list';
import DeleteDoNotCallList from '../modals/delete-do-not-call-list/delete-do-not-call-list';
import DoNotCallListDetailsHeader from './components/do-not-call-list-details-header/do-not-call-list-details-header';
import DoNotCallListDetailsTable from './components/do-not-call-list-details-table/do-not-call-list-details-table-container';
import DoNotCallListDetailsSearch from './components/do-not-call-list-details-search';
import DoNotCallListEmptyUI from '../do-not-call-list/components/do-not-call-list-empty-ui/do-not-call-list-empty-ui';
import AddPhoneNumber from '../modals/add-phone-number/add-phone-number';
import ImportViaCsv from '../modals/import-via-csv/import-via-csv';

import {
  executeOnErrorWithErrorCheck,
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../../../shared/utils';
import { NoCallDNCListFound } from '../../../../../../shared/svg/no-call-dnc-list-found';
import { NoDNCListFound } from '../../../../../../shared/svg';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';
import GlobalRestrictionModal from '../../../../../home/components/global-restriction-modal';

const DoNotCallListDetails: React.FC<IProps> = ({
  doNotCallListDetails,
  doNotCallListDetailsPaginationOptions,

  getDoNotCallListDetailsRequestStatus,
  getDoNotCallListDetailsRequestError,
  sendGetDoNotCallListDetailsRequest,
  resetGetDoNotCallListDetailsRequest,

  updateDoNotCallListRequestStatus,
  updateDoNotCallListRequestError,
  sendUpdateDoNotCallListRequest,
  resetUpdateDoNotCallListRequest,

  addPhoneNumberRequestStatus,
  addPhoneNumberRequestError,
  sendAddPhoneNumberRequest,
  resetAddPhoneNumberRequest,

  deletePhoneNumbersRequestStatus,
  deletePhoneNumbersRequestError,
  sendDeletePhoneNumbersRequest,
  resetDeletePhoneNumbersRequest,

  bulkDeletePhoneNumbersRequestStatus,
  bulkDeletePhoneNumbersRequestError,
  sendBulkDeletePhoneNumbersRequest,
  resetBulkDeletePhoneNumbersRequest,

  importPhoneNumbersViaCsvRequestStatus,
  importPhoneNumbersViaCsvRequestError,
  sendImportPhoneNumbersViaCsvRequest,
  resetImportPhoneNumbersViaCsvRequest,

  selectedPhoneNumbers,
  bulkSelectedPhoneNumbers,
  clearPhoneNumbersSelection,
  resetSelectedPhoneNumbers,

  agencyClients,
  sendGetAgencyClientsDropdownRequest,

  isDemoAccount,
  doNotCallListDetailsPayload,
}) => {
  const history = useHistory();
  const location = useLocation<{ row: DoNotCallList }>();
  const { listId } = useParams<{ listId: string }>();

  const [listName, setListName] = useState(location.state?.row?.name || '');
  const [client, setClient] = useState(location.state?.row?.client || null);
  const [totalCount, setTotalCount] = useState(0);

  const subtitle = client
    ? `${client.companyName}${client?.email ? ` (${client.email})` : ''}`
    : 'All Clients (Global)';

  const [filters, setFilters] = useState({
    page: 1,
    limit: 25,
    search: '',
  });

  const [resetSelected, setResetSelected] = useState(false);

  const [
    selectedPhoneNumber,
    setSelectedPhoneNumber,
  ] = useState<DoNotCallListPhoneNumber>(null);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddPhoneModal, setShowAddPhoneModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const [
    globalRestrictionModalContent,
    setGlobalRestrictionModalContent,
  ] = useState({
    show: false,
    title: '',
    content: '',
  });

  const hideGlobalRestrictionModal = () =>
    setGlobalRestrictionModalContent({ show: false, title: '', content: '' });

  const onShowImportModal = () => {
    if (isDemoAccount) {
      setGlobalRestrictionModalContent({
        show: true,
        title: 'Import is disabled in demo mode',
        content:
          'This is a demo account with sample data. Signup to import phone numbers to your do-not-call list.',
      });
      return;
    }
    setShowImportModal(true);
  };

  const handleResetSelected = (value = false) => setResetSelected(value);

  const fetchDetails = (overrideFilters = filters) => {
    sendGetDoNotCallListDetailsRequest({
      dncListId: listId,
      ...overrideFilters,
    });
  };

  const onFiltersChange = (
    partialFilters: Partial<typeof filters>,
    resetSelection = false,
  ) => {
    const updatedFilters = { ...filters, ...partialFilters };
    setFilters(updatedFilters);
    sendGetDoNotCallListDetailsRequest({
      dncListId: listId,
      ...updatedFilters,
    });

    if (resetSelection) {
      clearPhoneNumbersSelection();
      handleResetSelected(true);
    }
  };

  const onAction = (key: string, row: DoNotCallListPhoneNumber) => {
    if (key === 'delete') {
      setSelectedPhoneNumber(row);
      setShowDeleteModal(true);
    }
  };

  const onDeleteSubmit = () => {
    if (selectedPhoneNumber) {
      sendDeletePhoneNumbersRequest({
        dncListId: listId,
        ids: [selectedPhoneNumber.id],
      });
    } else if (bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected) {
      sendBulkDeletePhoneNumbersRequest({
        dncListId: listId,
        deSelectedIds: bulkSelectedPhoneNumbers.deSelectedPhoneNumberIds,
        phoneNumberFilter: { search: filters.search },
      });
    } else {
      sendDeletePhoneNumbersRequest({
        dncListId: listId,
        ids: selectedPhoneNumbers.map((p) => p.id),
      });
    }
  };

  const handleExport = async () => {
    if (isDemoAccount) {
      setGlobalRestrictionModalContent({
        show: true,
        title: 'Export is disabled in demo mode',
        content:
          'This is a demo account with sample data. Signup to export your do-not-call list.',
      });
      return;
    }

    const isBulkAction =
      bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected || false;

    const result = await api.post(`/call-dnc/dnclist/export`, {
      dncListId: listId,
      ...(filters.search !== '' && { search: filters.search }),
      isBulkAction,
      ...(!isBulkAction &&
        selectedPhoneNumbers?.length > 0 && {
          selectedIds: selectedPhoneNumbers.map((p) => p.id),
        }),
      ...(isBulkAction &&
        bulkSelectedPhoneNumbers?.deSelectedPhoneNumberIds?.length > 0 && {
          deSelectedIds: bulkSelectedPhoneNumbers.deSelectedPhoneNumberIds,
        }),
    });

    const { message, status } = result.payload;
    if (status === 0) {
      toaster.error(message);
    } else {
      toaster.success(
        message ||
          'Export started. You will receive an email with the exported data.',
      );
    }
  };

  useEffect(() => {
    if (doNotCallListDetailsPayload && !location.state?.row?.name) {
      setListName(doNotCallListDetailsPayload.name);
      setClient(doNotCallListDetailsPayload.client);
      setTotalCount(doNotCallListDetailsPayload?.totalCount);
    }
  }, [doNotCallListDetailsPayload, location.state?.row?.name]);

  useEffect(() => {
    if (listId) {
      fetchDetails();
      resetSelectedPhoneNumbers();
    }
    return () => {
      clearPhoneNumbersSelection();
    };
  }, []);

  useEffect(() => {
    executeOnRequestStatus({
      status: getDoNotCallListDetailsRequestStatus,
      onSuccess: () => {
        if (doNotCallListDetailsPaginationOptions) {
          setTotalCount(doNotCallListDetailsPaginationOptions.totalItems);
        }
        resetGetDoNotCallListDetailsRequest();
      },
      onFailed: () => {
        resetGetDoNotCallListDetailsRequest();
        executeOnErrorWithErrorCheck({
          error: getDoNotCallListDetailsRequestError,
          onError: () => {
            toaster.error(getDoNotCallListDetailsRequestError?.message);
          },
        });
      },
    });
  }, [getDoNotCallListDetailsRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updateDoNotCallListRequestStatus,
      onSuccess: () => {
        toaster.success('List modified successfully');
        setShowModifyModal(false);
        resetUpdateDoNotCallListRequest();
      },
      onFailed: () => {
        resetUpdateDoNotCallListRequest();
        setShowModifyModal(false);
        executeOnErrorWithErrorCheck({
          error: updateDoNotCallListRequestError,
          onError: () => {
            toaster.error(updateDoNotCallListRequestError?.message);
          },
        });
      },
    });
  }, [updateDoNotCallListRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: addPhoneNumberRequestStatus,
      onSuccess: () => {
        toaster.success('Phone number added successfully');
        setShowAddPhoneModal(false);
        resetAddPhoneNumberRequest();
        fetchDetails({ ...filters, page: 1 });
      },
      onFailed: () => {
        resetAddPhoneNumberRequest();
        executeOnErrorWithErrorCheck({
          error: addPhoneNumberRequestError,
          onError: () => {
            toaster.error(addPhoneNumberRequestError?.message);
          },
        });
      },
    });
  }, [addPhoneNumberRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: deletePhoneNumbersRequestStatus,
      onSuccess: () => {
        toaster.success('Phone number(s) deleted successfully');
        setShowDeleteModal(false);
        setSelectedPhoneNumber(null);
        clearPhoneNumbersSelection();
        handleResetSelected(true);
        resetDeletePhoneNumbersRequest();

        if (doNotCallListDetailsPaginationOptions?.itemCount === 1) {
          onFiltersChange({ page: 1 });
        } else {
          fetchDetails();
        }
      },
      onFailed: () => {
        resetDeletePhoneNumbersRequest();
        setShowDeleteModal(false);
        setSelectedPhoneNumber(null);
        clearPhoneNumbersSelection();
        handleResetSelected(true);
        executeOnErrorWithErrorCheck({
          error: deletePhoneNumbersRequestError,
          onError: () => {
            toaster.error(deletePhoneNumbersRequestError?.message);
          },
        });
      },
    });
  }, [deletePhoneNumbersRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: bulkDeletePhoneNumbersRequestStatus,
      onSuccess: () => {
        toaster.success('Phone numbers deleted successfully');
        setShowDeleteModal(false);
        clearPhoneNumbersSelection();
        handleResetSelected(true);
        resetBulkDeletePhoneNumbersRequest();
        fetchDetails({ ...filters, page: 1 });
      },
      onFailed: () => {
        resetBulkDeletePhoneNumbersRequest();
        setShowDeleteModal(false);
        clearPhoneNumbersSelection();
        handleResetSelected(true);
        executeOnErrorWithErrorCheck({
          error: bulkDeletePhoneNumbersRequestError,
          onError: () => {
            toaster.error(bulkDeletePhoneNumbersRequestError?.message);
          },
        });
      },
    });
  }, [bulkDeletePhoneNumbersRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: importPhoneNumbersViaCsvRequestStatus,
      onSuccess: () => {
        toaster.success(
          'You will shortly receive an email with the import report. (Estimate 30 min)',
        );
        setShowImportModal(false);
        resetImportPhoneNumbersViaCsvRequest();
        fetchDetails({ ...filters, page: 1 });
      },
      onFailed: () => {
        resetImportPhoneNumbersViaCsvRequest();
        setShowImportModal(false);
        executeOnErrorWithErrorCheck({
          error: importPhoneNumbersViaCsvRequestError,
          onError: () => {
            toaster.error(importPhoneNumbersViaCsvRequestError?.message);
          },
        });
      },
    });
  }, [importPhoneNumbersViaCsvRequestStatus]);

  const isDetailsLoading = getIsRequestPending(
    getDoNotCallListDetailsRequestStatus,
  );
  const isImportLoading = getIsRequestPending(
    importPhoneNumbersViaCsvRequestStatus,
  );

  const hasSelection =
    selectedPhoneNumbers?.length > 0 ||
    bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected;

  if (!hasPermission(Permissions.DNC_LIST_READ)) {
    history.push('/settings/call-dnc-list');
    return null;
  }

  const renderTableHeader = () => (
    <div className="do-not-contact-list-details--actions d-flex justify-content-between align-items-center mb-3">
      <div className="actions-left d-flex justify-content-center align-items-center">
        {isDetailsLoading && filters.search === '' ? (
          <SkeletonLoading width={300} height={32} />
        ) : (
          <DoNotCallListDetailsSearch
            filters={filters}
            onFiltersChange={onFiltersChange}
          />
        )}
      </div>

      <div className="actions-right d-flex justify-content-center align-items-center">
        {isDetailsLoading && filters.search === '' ? (
          <SkeletonLoading width={80} height={32} />
        ) : (
          <>
            {hasSelection && hasPermission(Permissions.DNC_LIST_DELETE) && (
              <DeleteButton
                label="Remove from List"
                handleDelete={() => setShowDeleteModal(true)}
              />
            )}

            {doNotCallListDetails?.length > 0 && (
              <ExportButton handleExport={handleExport} />
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderTableJSX = () => {
    if (
      doNotCallListDetails?.length === 0 &&
      !isDetailsLoading &&
      filters.search === ''
    ) {
      return null;
    }

    if (doNotCallListDetails?.length === 0 && !isDetailsLoading) {
      return (
        <>
          {renderTableHeader()}
          <DoNotCallListEmptyUI
            icon={<NoDNCListFound />}
            title="No Search Result"
            desc="We can't find any entries matching your search."
          />
        </>
      );
    }

    return (
      <>
        {renderTableHeader()}
        <DoNotCallListDetailsTable
          doNotCallListDetails={doNotCallListDetails}
          doNotCallListDetailsPaginationOptions={
            doNotCallListDetailsPaginationOptions
          }
          filters={filters}
          isLoading={isDetailsLoading}
          onAction={onAction}
          onFiltersChange={onFiltersChange}
          resetSelected={resetSelected}
          handleResetSelected={handleResetSelected}
        />
      </>
    );
  };

  const renderEmptyState = () => {
    if (
      doNotCallListDetails?.length === 0 &&
      !isDetailsLoading &&
      filters.search === ''
    ) {
      return (
        <DoNotCallListEmptyUI
          icon={<NoCallDNCListFound />}
          title="No Phone Numbers Added yet."
          desc="Your phone number list will appear here once you start adding the numbers."
        />
      );
    }
    return null;
  };

  return (
    <div className="do-not-contact-list-details">
      <DoNotCallListDetailsHeader
        listName={listName}
        totalCount={totalCount}
        subtitle={subtitle}
        isLoading={isDetailsLoading && !listName}
        onEdit={() => setShowModifyModal(true)}
        onAddPhoneNumber={() => setShowAddPhoneModal(true)}
        onShowImportModal={onShowImportModal}
      />

      {renderEmptyState() || renderTableJSX()}

      <AddModifyDoNotCallList
        show={showModifyModal}
        action="modify"
        isLoading={getIsRequestPending(updateDoNotCallListRequestStatus)}
        defaultDoNotCallList={{
          id: listId,
          name: listName,
          totalCount,
          updatedAt: '',
          client,
          user: null,
        }}
        agencyClients={agencyClients}
        sendGetAgencyClientsDropdownRequest={
          sendGetAgencyClientsDropdownRequest
        }
        onClose={() => setShowModifyModal(false)}
        onAddDoNotCallList={() => null}
        onUpdateDoNotCallList={(payload) => {
          setListName(payload.name);
          if (payload.makeGlobal) {
            setClient(null);
          } else if (payload.clientId) {
            const matched = agencyClients.find(
              (c) => c.id === payload.clientId,
            );
            if (matched) {
              setClient({
                id: matched.id,
                companyName: matched.companyName,
                firstName: matched.firstName,
                lastName: matched.lastName,
                email: matched.email,
              });
            }
          }
          sendUpdateDoNotCallListRequest(payload);
        }}
      />

      <DeleteDoNotCallList
        show={showDeleteModal}
        title="Delete Phone Number(s)"
        contents={[
          'Are you sure you want to delete the selected phone number(s)?',
          'Once deleted, these numbers will be removed from the do-not-call list.',
        ]}
        isLoading={
          getIsRequestPending(deletePhoneNumbersRequestStatus) ||
          getIsRequestPending(bulkDeletePhoneNumbersRequestStatus)
        }
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedPhoneNumber(null);
        }}
        onSubmit={onDeleteSubmit}
      />

      <AddPhoneNumber
        show={showAddPhoneModal}
        isLoading={getIsRequestPending(addPhoneNumberRequestStatus)}
        dncListId={listId}
        onClose={() => setShowAddPhoneModal(false)}
        onSubmit={sendAddPhoneNumberRequest}
      />

      <ImportViaCsv
        show={showImportModal}
        isLoading={isImportLoading}
        dncListId={listId}
        onCancel={() => setShowImportModal(false)}
        onSubmit={sendImportPhoneNumbersViaCsvRequest}
      />

      <GlobalRestrictionModal
        show={globalRestrictionModalContent.show}
        onClose={hideGlobalRestrictionModal}
        title={globalRestrictionModalContent.title}
        content={globalRestrictionModalContent.content}
        primaryBtnLabel="Sign up"
      />
    </div>
  );
};

export default DoNotCallListDetails;
